import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { EvCalculationChargingPerKilometer } from '../../@types/EvCalculation';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import type { ApiResponse } from '../../Http';
import { buildGetRequest } from '../../Http/requestTemplates';
import useHttp from '../../Http/useHttp';
import { EV_CALCULATION_CHARGING_PER_KILOMETER } from './QueryKeys';

const useEvCharchingCostPerKilometer = (
  id: string,
  kwhCost?: number,
  disabled?: boolean,
): [ApiResponse<EvCalculationChargingPerKilometer> | undefined, RequestStateQuery] => {
  const { httpJson } = useHttp();
  const queryClient = useQueryClient();

  const get = useCallback(async () => {
    const response = await httpJson<ApiResponse<EvCalculationChargingPerKilometer>>(
      `/api/ev-calculation/vehicle/charging/${id}/costPerKilometer${kwhCost ? `?kwhCost=${kwhCost}` : ''}`,
      buildGetRequest(),
    );

    // Handle the onSuccess logic here
    const exist = queryClient.getQueryData<ApiResponse<EvCalculationChargingPerKilometer>>([
      EV_CALCULATION_CHARGING_PER_KILOMETER,
      id,
      kwhCost ? kwhCost : response.response.description.costPerKwh.value,
    ]);

    if (!exist) {
      queryClient.setQueryData<ApiResponse<EvCalculationChargingPerKilometer>>(
        [
          EV_CALCULATION_CHARGING_PER_KILOMETER,
          id,
          kwhCost ? kwhCost : response.response.description.costPerKwh.value,
        ],
        { ...response },
      );
    }

    return response;
  }, [kwhCost, httpJson, id, queryClient]);

  const { data, isPending, isSuccess, isError } = useQuery<
    ApiResponse<EvCalculationChargingPerKilometer>
  >({
    queryKey: [EV_CALCULATION_CHARGING_PER_KILOMETER, id, kwhCost],
    queryFn: get,
    retry: false,
    refetchOnWindowFocus: false,
    gcTime: Number.POSITIVE_INFINITY,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: !disabled,
  });

  return [data, { isLoading: isPending, isSuccess, isError }];
};

export default useEvCharchingCostPerKilometer;
